import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ContentState,
  EditorState,
  CompositeDecorator,
  SelectionState,
} from 'draft-js';
import {
  submitPronunciationCheck,
  pollPronunciationResult,
} from '@src/apis/pronunciationCheck';
import {
  LANGUAGE_CODE,
  VOICE_PROVIDER,
  BREAK_TIME_REGEX,
} from '@src/constants/voice';
import { PACKAGE_FEATURES } from '@src/constants/package';
import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import { findWithRegexEditor } from '@src/services/editor';
import { PRONUNCIATION_DIALOG_TYPE } from '@src/containers/TTSNew/PronunciationCheckDialogs';
import useTotalCredits from '@src/hooks/useTotalCredits';
import {
  normalizeNswResponse,
  filterContainedItems,
  applyHighlightEntities,
  removeAllEntities,
  markWordAsFixed,
  markAllWordsAsFixed,
  getHighlightEntityKeys,
  getFixedWordEntityKeys,
  removeExtraEntities,
  calculatePronunciationCheckCredits,
} from '@src/services/pronunciationCheck';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  setNswItems,
  selectNswItem,
  setNswLoading,
  setNswSidebarOpen,
  markNswApplied,
  markAllNswApplied,
  syncNswItemsWithEntities,
  resetNsw,
} from '@src/redux/pronunciationCheck/actions';
import { countTextLength } from '@src/services/tts';
import { trackEvent } from '@src/services/moengage';
import {
  MOE_EVENTS,
  MOE_FEATURE_KEY,
  MOE_MODULE,
  MOE_PAYWALL_TYPE,
} from '@src/constants/moengage';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import actions from '@src/redux/actions';
import useUserInfo from './useTtsUser';

const usePronunciationCheck = ({
  editorState,
  onChangeEditorState,
  onChangeInputLength,
  highlightStrategy,
  fixedWordStrategy,
  HighlightWord,
  FixedWord,
}) => {
  const dispatch = useDispatch();
  const pendingContentRef = useRef(null);
  const contentStateRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [dialogType, setDialogType] = useState(null);
  const [noIssuesFound, setNoIssuesFound] = useState(false);
  const { ttsUser } = useUserInfo();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);

  const items = useSelector((state) => state.pronunciationCheck.items);
  const selectedIndex = useSelector(
    (state) => state.pronunciationCheck.selectedIndex,
  );
  const isLoading = useSelector((state) => state.pronunciationCheck.isLoading);
  const isOpen = useSelector((state) => state.pronunciationCheck.isOpen);
  const hasChecked = useSelector(
    (state) => state.pronunciationCheck.hasChecked,
  );
  const voice = useSelector((state) => state.synthesisRequest.voice);
  const voiceCode = voice?.code;
  const voiceName = voice?.name;
  const voiceLanguageCode = voice?.languageCode;
  const voiceProvider = voice?.provider;

  const isVbeeVietnameseVoice =
    [VOICE_PROVIDER.VBEE, VOICE_PROVIDER.VBEE_VOICE_CLONING].includes(
      voiceProvider,
    ) && voiceLanguageCode === LANGUAGE_CODE.VIETNAMESE;
  const openSentences = useSelector(
    (state) => state.synthesisRequest.openSentences,
  );
  const { relateDataRequest } = useSelector((state) => state.synthesisRequest);

  const { usingPackage } = useSelector((state) => state.user);
  const { totalCredits } = useTotalCredits();
  // Hits fire here at the gate; the dialogs component fires the CTA
  const { trackHit: trackPaywallHit } = usePaywallTracking({
    module: MOE_MODULE.TTS,
    featureKey: MOE_FEATURE_KEY.PRONUNCIATION_CHECK,
  });

  const contentState = editorState.getCurrentContent();
  contentStateRef.current = contentState;
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const plainText = contentState.getPlainText('\n');
  const isEditorEmpty = !plainText.trim();
  const prevPlainTextRef = useRef(plainText);

  const breakTimeStrategy = useCallback((contentBlock, callback) => {
    findWithRegexEditor(BREAK_TIME_REGEX, contentBlock, callback);
  }, []);

  const createDecorator = useCallback(
    () =>
      new CompositeDecorator([
        { strategy: highlightStrategy, component: HighlightWord },
        { strategy: fixedWordStrategy, component: FixedWord },
        { strategy: breakTimeStrategy, component: BreakTimeHighlight },
      ]),
    [
      highlightStrategy,
      fixedWordStrategy,
      HighlightWord,
      FixedWord,
      breakTimeStrategy,
    ],
  );

  const updateEditor = useCallback(
    (newContentState) => {
      const currentEditorState = editorStateRef.current;
      const decorator = createDecorator();
      const newEditorState = EditorState.createWithContent(
        newContentState,
        decorator,
      );

      // Validate that old selection still fits the new content
      const oldSelection = currentEditorState.getSelection();
      const hasFocus = oldSelection.getHasFocus();
      const anchorKey = oldSelection.getAnchorKey();
      const newBlock = newContentState.getBlockForKey(anchorKey);
      let safeSelection;
      if (newBlock) {
        const anchorOffset = Math.min(
          oldSelection.getAnchorOffset(),
          newBlock.getLength(),
        );
        if (!hasFocus) {
          // Editor not focused — collapse to a single point to avoid
          // re-highlighting the previous selection range
          safeSelection = SelectionState.createEmpty(anchorKey).merge({
            anchorOffset,
            focusOffset: anchorOffset,
          });
        } else {
          const focusKey = oldSelection.getFocusKey();
          const focusBlock = newContentState.getBlockForKey(focusKey);
          const focusOffset = focusBlock
            ? Math.min(oldSelection.getFocusOffset(), focusBlock.getLength())
            : anchorOffset;
          safeSelection = oldSelection.merge({
            anchorOffset,
            focusKey: focusBlock ? focusKey : anchorKey,
            focusOffset,
          });
        }
      } else {
        // Anchor block no longer exists — collapse to start
        const firstBlock = newContentState.getFirstBlock();
        safeSelection = SelectionState.createEmpty(firstBlock.getKey());
      }

      const withSelection = EditorState.forceSelection(
        newEditorState,
        safeSelection,
      );
      onChangeEditorState(withSelection);
    },
    [onChangeEditorState, createDecorator],
  );

  const scrollToWord = useCallback((entityKey) => {
    const el = document.querySelector(`[data-entity-key="${entityKey}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  // Count items before itemIndex matching the applied flag
  const countBefore = useCallback(
    (itemIndex, applied) =>
      items
        .slice(0, itemIndex)
        .reduce((acc, item) => acc + (item?.applied === applied ? 1 : 0), 0),
    [items],
  );

  const getHighlightIndex = useCallback(
    (itemIndex) => countBefore(itemIndex, false),
    [countBefore],
  );

  const getFixedWordIndex = useCallback(
    (itemIndex) => countBefore(itemIndex, true),
    [countBefore],
  );

  // Reverse: entity key → items index (find nth item matching applied flag)
  const getItemIndexByEntityKey = useCallback(
    (entityKey, cs, getKeys, applied) => {
      const targetIdx = getKeys(cs).indexOf(entityKey);
      if (targetIdx === -1) return -1;
      const filtered = items.map((item, i) => ({ item, i }));
      const matching = filtered.filter(
        ({ item }) => !!item?.applied === applied,
      );
      return matching[targetIdx]?.i ?? -1;
    },
    [items],
  );

  const getItemIndexFromHighlightKey = useCallback(
    (entityKey, cs) =>
      getItemIndexByEntityKey(entityKey, cs, getHighlightEntityKeys, false),
    [getItemIndexByEntityKey],
  );

  const getItemIndexFromFixedWordKey = useCallback(
    (entityKey, cs) =>
      getItemIndexByEntityKey(entityKey, cs, getFixedWordEntityKeys, true),
    [getItemIndexByEntityKey],
  );

  const hasAppliedItems = items?.length && items.some((item) => item.applied);
  const aiPronunciationCheckCredits = getFeatureValue(
    FEATURE_KEYS.AI_PRONUNCIATION_CHECK_CREDIT_COST,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const estimatedCredits = calculatePronunciationCheckCredits(
    aiPronunciationCheckCredits,
  );

  const cancelCheck = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(setNswLoading(false));
  }, [dispatch]);

  const performCheck = useCallback(async () => {
    if (isEditorEmpty) return;

    cancelCheck();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    setNoIssuesFound(false);
    dispatch(setNswLoading(true));
    dispatch(setNswItems([]));

    try {
      const submitResponse = await submitPronunciationCheck(
        plainText,
        voiceCode,
        signal,
      );
      const { nswRequestId } = submitResponse.result;
      const rawResponse = await pollPronunciationResult(nswRequestId, signal);
      const { items: normalized, text: responseText } =
        normalizeNswResponse(rawResponse);
      const filtered = filterContainedItems(normalized);

      trackEvent(
        MOE_EVENTS.PRONUNCIATION_CHECK_COMPLETED,
        {
          issues_found: filtered.length,
          text_length: countTextLength(plainText),
        },
        { withIsFirst: true },
      );

      if (filtered.length === 0) {
        setNoIssuesFound(true);
        dispatch(setNswLoading(false));
        return;
      }

      const currentPlainText = contentStateRef.current.getPlainText('\n');
      const isTextChanged =
        responseText != null && responseText !== currentPlainText;
      const baseContent = isTextChanged
        ? ContentState.createFromText(responseText)
        : contentStateRef.current;

      const cleanContent = removeAllEntities(baseContent);
      const highlightedContent = applyHighlightEntities(cleanContent, filtered);
      pendingContentRef.current = contentState;
      dispatch(setNswItems(filtered));
      if (filtered?.length) dispatch(setNswSidebarOpen(true));
      updateEditor(highlightedContent);

      if (isTextChanged && onChangeInputLength) {
        onChangeInputLength(countTextLength(responseText));
      }

      requestAnimationFrame(() => {
        const keys = getHighlightEntityKeys(highlightedContent);
        if (keys[0]) scrollToWord(keys[0]);
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      setDialogType(PRONUNCIATION_DIALOG_TYPE.ERROR);
    } finally {
      if (!signal.aborted) {
        dispatch(setNswLoading(false));
      }
    }
  }, [
    isEditorEmpty,
    plainText,
    contentState,
    voiceCode,
    dispatch,
    updateEditor,
    cancelCheck,
    onChangeInputLength,
  ]);

  const check = useCallback(() => {
    if (isEditorEmpty) return;

    if (!isVbeeVietnameseVoice) {
      setDialogType(PRONUNCIATION_DIALOG_TYPE.ONLY_VIETNAMESE);
      return;
    }

    const hasPronunciationFeature = ttsUser?.features?.includes(
      PACKAGE_FEATURES.AI_CUSTOMIZATION_PRONUNCIATION,
    );
    if (!hasPronunciationFeature) {
      trackPaywallHit(MOE_PAYWALL_TYPE.PREMIUM_AI);
      setDialogType(PRONUNCIATION_DIALOG_TYPE.UPGRADE_PACKAGE);
      return;
    }

    const maxLength = ttsUser?.maxLengthInputText || 0;
    const characters = countTextLength(plainText);
    if (maxLength > 0 && characters > maxLength) {
      trackPaywallHit(MOE_PAYWALL_TYPE.MAX_CHARS, {
        characters,
        maxCharacters: maxLength,
      });
      setDialogType(PRONUNCIATION_DIALOG_TYPE.MAX_CHARS_EXCEEDED);
      return;
    }

    if (estimatedCredits > 0 && totalCredits < estimatedCredits) {
      trackPaywallHit(MOE_PAYWALL_TYPE.NOT_ENOUGH_CREDITS, {
        creditsNeeded: estimatedCredits,
        creditsRemaining: totalCredits,
      });
      setDialogType(PRONUNCIATION_DIALOG_TYPE.NOT_ENOUGH_CREDITS);
      return;
    }

    if (hasAppliedItems) {
      setDialogType(PRONUNCIATION_DIALOG_TYPE.RECHECK_CONFIRMATION);
      return;
    }

    performCheck();
  }, [
    isEditorEmpty,
    voiceLanguageCode,
    usingPackage,
    ttsUser,
    plainText,
    totalCredits,
    estimatedCredits,
    hasAppliedItems,
    performCheck,
    dispatch,
    trackPaywallHit,
  ]);

  const confirmRecheck = useCallback(() => {
    setDialogType(null);
    pendingContentRef.current = contentStateRef.current;
    performCheck();
  }, [updateEditor, dispatch, performCheck]);

  const closeDialog = useCallback(() => {
    setDialogType(null);
  }, []);

  const applySingle = useCallback(
    (inputValue) => {
      const currentItem = items[selectedIndex];
      if (!currentItem) return;

      let entityKey;
      let newContent;

      if (currentItem.applied) {
        const fixedKeys = getFixedWordEntityKeys(contentState);
        const fixedIdx = getFixedWordIndex(selectedIndex);
        entityKey = fixedKeys[fixedIdx];
        if (!entityKey) return;
        newContent = markWordAsFixed(contentState, entityKey, inputValue);
        pendingContentRef.current = contentState;
        updateEditor(newContent);
      } else {
        const keys = getHighlightEntityKeys(contentState);
        const highlightIdx = getHighlightIndex(selectedIndex);
        entityKey = keys[highlightIdx];
        if (!entityKey) return;
        newContent = markWordAsFixed(contentState, entityKey, inputValue);
        pendingContentRef.current = contentState;
        updateEditor(newContent);
        dispatch(markNswApplied(selectedIndex));
      }

      let nextIndex = items.findIndex(
        (item, i) => i > selectedIndex && !item.applied,
      );
      if (nextIndex === -1) {
        nextIndex = items.findIndex(
          (item, i) => i < selectedIndex && !item.applied,
        );
      }
      if (nextIndex !== -1) {
        dispatch(selectNswItem(nextIndex));
        const nextKeys = getHighlightEntityKeys(newContent);
        let hIdx = 0;
        for (let i = 0; i < nextIndex; i += 1) {
          if (i !== selectedIndex && !items[i]?.applied) hIdx += 1;
        }
        if (nextKeys[hIdx]) scrollToWord(nextKeys[hIdx]);
      } else if (!currentItem.applied) {
        setNoIssuesFound(true);
      }
      if (inputValue !== currentItem?.pronunciation) {
        dispatch(
          actions.synthesisRequest.updateDataRelatedRequest({
            ...relateDataRequest,
            wordChange: true,
          }),
        );
      }
    },
    [
      items,
      selectedIndex,
      contentState,
      dispatch,
      updateEditor,
      scrollToWord,
      getHighlightIndex,
      getFixedWordIndex,
    ],
  );

  const applyAll = useCallback(
    (pronunciation) => {
      const currentItem = items[selectedIndex];
      if (!currentItem) return;

      const { word } = currentItem;
      const newContent = markAllWordsAsFixed(contentState, word, pronunciation);
      pendingContentRef.current = contentState;
      updateEditor(newContent);
      dispatch(markAllNswApplied(word, pronunciation));

      const isUnappliedOther = (item) => !item.applied && item.word !== word;
      let nextIdx = items.findIndex(
        (item, i) => i > selectedIndex && isUnappliedOther(item),
      );
      if (nextIdx === -1) {
        nextIdx = items.findIndex((item) => isUnappliedOther(item));
      }
      if (nextIdx !== -1) {
        dispatch(selectNswItem(nextIdx));
        const nextKeys = getHighlightEntityKeys(newContent);
        let hIdx = 0;
        for (let i = 0; i < nextIdx; i += 1) {
          if (!items[i].applied && items[i].word !== word) hIdx += 1;
        }
        if (nextKeys[hIdx]) scrollToWord(nextKeys[hIdx]);
      } else {
        setNoIssuesFound(true);
      }
      if (pronunciation !== currentItem?.pronunciation) {
        dispatch(
          actions.synthesisRequest.updateDataRelatedRequest({
            ...relateDataRequest,
            wordChange: true,
          }),
        );
      }
    },
    [items, selectedIndex, contentState, dispatch, updateEditor, scrollToWord],
  );

  const scrollToItem = useCallback(
    (idx) => {
      if (idx === -1) return;
      dispatch(selectNswItem(idx));
      const keys = getHighlightEntityKeys(contentState);
      const hIdx = getHighlightIndex(idx);
      if (keys[hIdx]) scrollToWord(keys[hIdx]);
    },
    [contentState, dispatch, scrollToWord, getHighlightIndex],
  );

  const goToNext = useCallback(() => {
    const nextIdx = items.findIndex(
      (item, i) => i > selectedIndex && !item.applied,
    );
    scrollToItem(nextIdx);
  }, [items, selectedIndex, scrollToItem]);

  const goToPrev = useCallback(() => {
    const prevIdx = items.findLastIndex(
      (item, i) => i < selectedIndex && !item.applied,
    );
    scrollToItem(prevIdx);
  }, [items, selectedIndex, scrollToItem]);

  const selectByEntityKey = useCallback(
    (entityKey) => {
      const latestContentState = contentStateRef.current;
      let itemIndex = getItemIndexFromHighlightKey(
        entityKey,
        latestContentState,
      );
      if (itemIndex === -1) {
        itemIndex = getItemIndexFromFixedWordKey(entityKey, latestContentState);
      }
      if (itemIndex !== -1) {
        dispatch(selectNswItem(itemIndex));
        if (!isOpen) dispatch(setNswSidebarOpen(true));
      }
    },
    [
      dispatch,
      getItemIndexFromHighlightKey,
      getItemIndexFromFixedWordKey,
      isOpen,
    ],
  );

  const close = useCallback(() => {
    dispatch(setNswSidebarOpen(false));
  }, [dispatch]);

  const openSideBox = useCallback(() => {
    dispatch(setNswSidebarOpen(true));
    const keys = getHighlightEntityKeys(contentState);
    const hIdx = getHighlightIndex(selectedIndex);
    if (keys[hIdx]) scrollToWord(keys[hIdx]);
  }, [contentState, selectedIndex, scrollToWord, getHighlightIndex, dispatch]);

  const itemsRef = useRef(items);
  const hasCheckedRef = useRef(hasChecked);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  useEffect(() => {
    if (items?.length === 0 && isOpen) {
      dispatch(setNswSidebarOpen(false));
    }
  }, [items, isOpen]);

  useEffect(() => {
    hasCheckedRef.current = hasChecked;
  }, [hasChecked]);

  useEffect(() => {
    if (prevPlainTextRef.current !== plainText) {
      prevPlainTextRef.current = plainText;
      setNoIssuesFound(false);
    }
  }, [plainText]);

  useEffect(() => {
    // Skip sync while contentState hasn't changed from when we initiated
    // a programmatic update. Multiple Redux dispatches can trigger re-renders
    // before the parent propagates the new editorState back to us.
    if (pendingContentRef.current != null) {
      if (contentState === pendingContentRef.current) {
        return;
      }
      // New contentState arrived — clear pending flag
      pendingContentRef.current = null;
    }

    if (!hasCheckedRef.current) return;

    const currentItems = itemsRef.current;
    if (!currentItems.length) return;

    const unappliedItems = currentItems.filter((item) => !item.applied);
    const appliedItems = currentItems.filter((item) => item.applied);

    const highlightKeys = getHighlightEntityKeys(contentState);
    const fixedKeys = getFixedWordEntityKeys(contentState);

    const highlightChanged = highlightKeys.length !== unappliedItems.length;
    const fixedChanged = fixedKeys.length !== appliedItems.length;

    if (!highlightChanged && !fixedChanged) return;

    // User undo → entity restored but item already deleted → remove extra entities
    const hasExtraEntities =
      highlightKeys.length > unappliedItems.length ||
      fixedKeys.length > appliedItems.length;
    if (hasExtraEntities) {
      pendingContentRef.current = contentState;
      const cleanedContent = removeExtraEntities(contentState, currentItems);
      updateEditor(cleanedContent);
      return;
    }

    // Entity deleted (user edit/delete text) → sync items to remaining entities
    const getEntityData = (key) => {
      try {
        return contentState.getEntity(key).getData();
      } catch {
        return null;
      }
    };
    const survivingIds = [
      ...highlightKeys.map(getEntityData),
      ...fixedKeys.map(getEntityData),
    ].filter(Boolean);
    if (survivingIds.length < currentItems.length) {
      setNoIssuesFound(false);
    }
    dispatch(syncNswItemsWithEntities(survivingIds));
  }, [contentState, dispatch, updateEditor]); // items & hasChecked read from refs

  // Reset on unmount
  useEffect(
    () => () => {
      dispatch(resetNsw());
    },
    [dispatch],
  );

  // Reset when voice changes
  const voiceCodeRef = useRef(voiceCode);
  useEffect(() => {
    if (voiceCodeRef.current === voiceCode) return;
    voiceCodeRef.current = voiceCode;

    if (!isOpen) return;
    pendingContentRef.current = contentStateRef.current;
    dispatch(setNswSidebarOpen(false));
    const cleanContent = removeAllEntities(contentStateRef.current);
    updateEditor(cleanContent);
    dispatch(resetNsw());
  }, [voiceCode, isOpen, dispatch, updateEditor]);

  const currentItem = isOpen ? items[selectedIndex] || null : null;
  const currentEntityKey = (() => {
    if (!currentItem) return null;
    if (currentItem.applied) {
      const keys = getFixedWordEntityKeys(contentState);
      return keys[getFixedWordIndex(selectedIndex)] || null;
    }
    const keys = getHighlightEntityKeys(contentState);
    return keys[getHighlightIndex(selectedIndex)] || null;
  })();
  const remainingCount = items.filter((item) => !item.applied).length;

  const sameWordCount = currentItem
    ? items.filter((item) => item.word === currentItem.word).length
    : 0;
  return {
    items,
    selectedIndex,
    isLoading,
    isOpen,
    isEditorEmpty,
    hasChecked,
    openSentences,
    currentItem,
    currentEntityKey,
    remainingCount,
    sameWordCount,
    check,
    applySingle,
    applyAll,
    goToNext,
    goToPrev,
    selectByEntityKey,
    close,
    openSideBox,
    estimatedCredits,
    dialogType,
    closeDialog,
    confirmRecheck,
    isVbeeVietnameseVoice,
    voiceName,
    cancelCheck,
    maxLengthInputText: ttsUser?.maxLengthInputText || 0,
    noIssuesFound,
  };
};

export default usePronunciationCheck;
